import { computarSaldoCapex } from "./capex/getSaldoCapexTarget";
import { claveTargetCapex, getSaldosCapexPorTarget } from "./capex/getSaldosCapexPorTarget";
import { posicionesCuotas } from "./cuotasUtil";
import { client } from "./supabase";

// ─── PLAN PAGOS ACTIVOS (cuotas de un activo CapEx planificado) ───────────────
//
// Espejo de planCobros.js (rama "pagos"), pero colgado de activos(id) vía
// plan_pagos_activos.activo_id en vez de facturas_sii_recibidas. Doctrina (ver
// ARQUITECTURA 4.19.1 / 4.2.mm / 10.100): pagar una cuota es CAJA PURA
// (movimiento_bancario_id + fecha_pago_real + estado='pagada'); la activación /
// depreciación del activo va por su FACTURA (activos.fecha_compra), aparte.

export const getPlanPagosActivos = async (activoId) => {
  const { data, error } = await client
    .from("plan_pagos_activos")
    .select("*")
    .eq("activo_id", activoId)
    .order("numero_cuota");
  if (error) throw error;
  return data || [];
};

// Set de activo_id que tienen AL MENOS una cuota en el plan (cualquier estado). Necesario aparte de
// "tiene cuotas pendientes": si el plan está completo (todas pagadas), el activo sigue teniendo plan
// y NO debe volver a proyectar la línea única. Lo usa la bifurcación de CapEx en FlujoCajaV2.
export const getActivosConPlan = async (pais) => {
  const { data, error } = await client.from("plan_pagos_activos").select("activo_id").eq("pais", pais);
  if (error) throw error;
  return new Set((data || []).map((r) => r.activo_id));
};

// Conteo de cuotas por activo (cualquier estado) para una lista de ids, en una sola query. Alimenta
// el indicador "plan de cuotas (N)" del modal Inversión de Edificios. Devuelve { activo_id: count }.
export const getPlanCountsByActivo = async (activoIds) => {
  if (!activoIds || activoIds.length === 0) return {};
  const { data, error } = await client.from("plan_pagos_activos").select("activo_id").in("activo_id", activoIds);
  if (error) throw error;
  const counts = {};
  for (const row of data || []) counts[row.activo_id] = (counts[row.activo_id] || 0) + 1;
  return counts;
};

export const crearPlanPagosActivos = async (cuotas) => {
  const { data, error } = await client.from("plan_pagos_activos").insert(cuotas).select();
  if (error) throw error;
  return data || [];
};

// RPC ATÓMICA (2c Fase 2) — máquina de estados del plan de pago de un activo, todo-o-nada en la base
// (10.190/10.239). Reemplaza el Promise.all no-atómico del modal. `estado`: 'plan' | 'fecha_unica' |
// 'cero'. `cuotas` en NETO ([{ id?, monto_cuota, fecha_vencimiento }], solo para 'plan'; el modal
// convierte bruto→neto — la RPC NO convierte IVA/TC). `fechaUnica` solo para 'fecha_unica'. La RPC
// valida virgen/Σ=costo/candados y RAISEa (error visible, 10.177). Ver migración
// 20260913_capex_guardar_plan_pagos_activo_rpc.sql.
export const guardarPlanPagosActivo = async ({
  activoId,
  estado,
  cuotas = [],
  fechaUnica = null,
  usuarioId = null,
}) => {
  const { data, error } = await client.rpc("guardar_plan_pagos_activo", {
    p_activo_id: activoId,
    p_estado: estado,
    p_cuotas: cuotas,
    p_fecha_unica: fechaUnica,
    p_usuario_id: usuarioId,
  });
  if (error) throw error;
  return data;
};

export const actualizarCuotaActivo = async (id, cambios) => {
  const { data, error } = await client.from("plan_pagos_activos").update(cambios).eq("id", id).select().single();
  if (error) throw error;
  return data;
};

// Borra UNA cuota por id. El candado es la BASE (trigger BEFORE DELETE, migración
// 20260912_capex_candados_crud_delete_moneda_lote_activo.sql — 10.190/cutover 2a): la DB RECHAZA el
// borrado si la cuota tiene un evento VIGENTE en `pagos_capex` (reversado_at IS NULL). Ya NO se lee el
// flag `estado`; el error del trigger propaga visible (10.177/10.170). Si no existe, data vacío → error propio.
export const eliminarCuotaActivo = async (id) => {
  const { data, error } = await client.from("plan_pagos_activos").delete().eq("id", id).select();
  if (error) throw error;
  if (!data || data.length === 0) throw new Error(`No se pudo borrar la cuota ${id} (no existe).`);
  return data[0];
};

// ─── Cuotas futuras pendientes (proyección CapEx) ─────────────────────────────
//
// Lee plan_pagos_activos + join al activo (FK real). NO se puede embeber el edificio: activos.destino_id
// es polimórfico (edificio/cartelería/empleado) y no tiene FK a edificios — el nombre del edificio lo
// resuelve el consumidor (FlujoCajaV2, commit 2) vía edificiosMap[destino_id], igual que mapCapex.
// Filtra estado='pendiente' + país; SIN filtro de fecha → incluye vencidas (patrón 10.155). El join
// !inner + eliminado=false descarta cuotas de activos borrados (soft-delete no dispara el CASCADE).
// `activoId` opcional: acota la carga a las cuotas de UN activo (refetch dirigido tras editar su plan).
// `saldosCapex` opcional (CUTOVER Fase 2b): el Map de getSaldosCapexPorTarget(pais); si no se pasa, se
// fetchea acá (una query batch, sin N+1, 10.234) — el modal lo llama sin pasarlo.
//
// CUTOVER Fase 2b: "pendiente" ya NO es el flag `estado`, es saldoRestante > tolerancia derivado del
// subledger `pagos_capex` (computarSaldoCapex, en la moneda del target). En todo-o-nada da IDÉNTICO al
// flag (aplicado 0 → saldoRestante=monto_cuota; aplicado=monto_cuota → 0, se excluye). El flag NO se
// apaga (queda en paralelo, se retira en Fase 4). Cada fila lleva `_saldoRestante` para el monto proyectado.
export const getCuotasActivosFuturas = async (pais, { activoId, saldosCapex } = {}) => {
  let query = client
    .from("plan_pagos_activos")
    .select(
      `
      *,
      activo:activos!inner(
        id, pais, destino_id, destino_tipo, tipo_activo, moneda, costo_unitario, exento_iva, eliminado, proveedor_id,
        proveedor:proveedores(nombre)
      )
    `
    )
    .eq("pais", pais)
    .eq("activo.eliminado", false);
  if (activoId != null) query = query.eq("activo_id", activoId);
  const { data, error } = await query.order("fecha_vencimiento");
  if (error) throw error;
  const todas = data || [];
  if (todas.length === 0) return todas;

  // Filtro ledger-based: saldoRestante > tolerancia (moneda del target). Reemplaza el .eq(estado,'pendiente').
  const saldos = saldosCapex || (await getSaldosCapexPorTarget(pais));
  const cuotas = todas
    .map((c) => {
      const aplicado = saldos.get(claveTargetCapex("cuota_activo", c.id))?.aplicadoTarget || 0;
      const { saldo, saldada } = computarSaldoCapex(c.monto_cuota, aplicado);
      return { ...c, _aplicadoTarget: aplicado, _saldoRestante: saldo, _saldada: saldada };
    })
    .filter((c) => !c._saldada);
  if (cuotas.length === 0) return cuotas;

  // `total_cuotas` = total del PLAN (todas las cuotas, cualquier estado) para el label "cuota N/M".
  // Se trae el plan COMPLETO por activo (id + numero_cuota + fecha_vencimiento) para derivar además
  // la POSICIÓN ordinal (`_posicion`) por (fecha_vencimiento, numero_cuota) — el número que se muestra,
  // decoupled del numero_cuota crudo append-only (10.1, posicionesCuotas). NUNCA se muestra numero_cuota.
  const activoIds = [...new Set(cuotas.map((c) => c.activo_id).filter((id) => id != null))];
  const { data: todasLasCuotas, error: errTotal } = await client
    .from("plan_pagos_activos")
    .select("id, activo_id, numero_cuota, fecha_vencimiento")
    .in("activo_id", activoIds);
  if (errTotal) throw errTotal;
  const totalPorActivo = {};
  const cuotasPorActivo = {};
  for (const row of todasLasCuotas || []) {
    totalPorActivo[row.activo_id] = (totalPorActivo[row.activo_id] || 0) + 1;
    (cuotasPorActivo[row.activo_id] = cuotasPorActivo[row.activo_id] || []).push(row);
  }
  const posPorActivo = {};
  for (const [aid, filas] of Object.entries(cuotasPorActivo)) posPorActivo[aid] = posicionesCuotas(filas);
  return cuotas.map((c) => ({
    ...c,
    total_cuotas: totalPorActivo[c.activo_id] || 0,
    _posicion: posPorActivo[c.activo_id]?.get(c.id) || null,
  }));
};
